import React, { useEffect, useState } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import { cancelCampaign } from '../../services/pushApi';
import { theme } from '../../styles/theme';
import { subscribeCampaign, subscribeRecipients } from '../../services/pushCampaigns';
import {
  TONE_COLORS,
  campaignStatusLabel,
  computeCampaignMetrics,
  formatDateTime,
  percent,
  recipientSituation,
} from '../../services/pushMetrics';
import { findPushScreen } from '../../config/pushScreens';
import { TTL_OPTIONS, findPushChannel } from '../../config/pushChannels';
import type { PushCampaign, PushRecipient } from '../../types/push';

interface Props {
  campaignId: string;
  onBack: () => void;
  onDuplicate: (campaign: PushCampaign) => void;
}

type RecipientFilter = 'all' | 'opened' | 'notOpened' | 'undeliverable' | 'failed';

const RECIPIENT_FILTERS: { value: RecipientFilter; label: string; matches: (r: PushRecipient) => boolean }[] = [
  { value: 'all', label: 'Todos', matches: () => true },
  { value: 'opened', label: 'Abriram', matches: r => !!r.openedAt },
  { value: 'notOpened', label: 'Não abriram', matches: r => r.status === 'sent' && !r.openedAt },
  { value: 'undeliverable', label: 'Não entregáveis', matches: r => r.status === 'invalid_token' || r.status === 'skipped_invalid' },
  { value: 'failed', label: 'Falhas', matches: r => r.status === 'failed' },
];

const normalizeText = (text: string) => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Detalhe de uma campanha: funil, motivos de não entrega, opções e cada aparelho. Atualiza ao vivo. */
const PushCampaignDetail: React.FC<Props> = ({ campaignId, onBack, onDuplicate }) => {
  const [campaign, setCampaign] = useState<PushCampaign | null>(null);
  const [recipients, setRecipients] = useState<PushRecipient[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<RecipientFilter>('all');
  const [search, setSearch] = useState('');
  const [canceling, setCanceling] = useState(false);

  // O status muda sozinho na tela (onSnapshot) depois que a API cancela.
  const handleCancel = async () => {
    if (!campaign?.scheduledAt) return;
    if (!window.confirm(`Cancelar o envio agendado para ${formatDateTime(campaign.scheduledAt)}?`)) return;
    try {
      setCanceling(true);
      await cancelCampaign(campaignId);
    } catch (error) {
      alert('Não foi possível cancelar: ' + (error instanceof Error ? error.message : 'Erro desconhecido'));
    } finally {
      setCanceling(false);
    }
  };

  useEffect(() => {
    const onError = (error: Error) => {
      console.error('❌ Erro ao carregar campanha:', error);
      setLoading(false);
    };
    const stopCampaign = subscribeCampaign(campaignId, (value) => {
      setCampaign(value);
      setLoading(false);
    }, onError);
    const stopRecipients = subscribeRecipients(campaignId, setRecipients, onError);
    return () => {
      stopCampaign();
      stopRecipients();
    };
  }, [campaignId]);

  if (loading) return <div style={styles.card}>Carregando...</div>;
  if (!campaign) {
    return (
      <div style={styles.card}>
        <button style={styles.backButton} onClick={onBack}><ArrowBackIcon style={{ fontSize: 18 }} /> Histórico</button>
        <p>Campanha não encontrada.</p>
      </div>
    );
  }

  const metrics = computeCampaignMetrics(recipients);
  // Depois de 6 meses os destinatários são apagados: vale o que foi congelado na campanha.
  const recipientsGone = recipients.length === 0 && (campaign.stats?.targeted ?? 0) > 0;
  const stats = campaign.stats;
  const targeted = recipientsGone ? stats?.targeted ?? 0 : metrics.targeted;
  const sent = recipientsGone ? stats?.sent ?? 0 : metrics.sent;
  const trackable = recipientsGone ? stats?.trackable ?? 0 : metrics.trackable;
  const opened = recipientsGone ? stats?.opened ?? 0 : metrics.opened;
  const trackableAndroid = recipientsGone ? stats?.trackableAndroid ?? 0 : metrics.trackableAndroid;
  const receivedAndroid = recipientsGone ? stats?.receivedAndroid ?? 0 : metrics.receivedAndroid;

  const status = campaignStatusLabel(campaign);
  const query = normalizeText(search.trim());
  const activeFilter = RECIPIENT_FILTERS.find(item => item.value === filter) || RECIPIENT_FILTERS[0];
  const shownRecipients = recipients
    .filter(activeFilter.matches)
    .filter(r => !query || normalizeText(`${r.businessName} ${r.deviceLabel}`).includes(query))
    .sort((a, b) => a.businessName.localeCompare(b.businessName, 'pt-BR'));

  const customData = Object.entries(campaign.customData);
  const options: [string, React.ReactNode][] = [
    ['Título', campaign.title],
    ['Mensagem', campaign.body],
    ['Imagem', campaign.imageUrl ? <img src={campaign.imageUrl} alt="" style={styles.thumbnail} /> : 'Sem imagem'],
    ['Tela de destino', campaign.screen ? findPushScreen(campaign.screen)?.label || campaign.screen : 'Nenhuma — só abre o app'],
    ['Som', campaign.sound ? 'Ligado' : 'Desligado'],
    ['Canal Android', findPushChannel(campaign.androidChannelId)?.label || campaign.androidChannelId],
    ['Validade', TTL_OPTIONS.find(option => option.value === campaign.ttlSeconds)?.label || `${campaign.ttlSeconds}s`],
    ['Dados personalizados', customData.length ? customData.map(([key, value]) => `${key} = ${value}`).join(', ') : '—'],
    ['Público', campaign.audienceLabel],
  ];

  const funnel = [
    {
      label: 'Público',
      value: String(targeted),
      detail: (stats?.users ?? 0) === 1 ? '1 negócio' : `${stats?.users ?? 0} negócios`,
    },
    { label: 'Enviadas', value: String(sent), detail: `${percent(sent, targeted)} do público` },
    {
      label: 'Recebidas (Android)',
      value: trackableAndroid ? String(receivedAndroid) : '—',
      detail: trackableAndroid ? `${percent(receivedAndroid, trackableAndroid)} de ${trackableAndroid} rastreáveis` : 'app 1.12+',
    },
    {
      label: 'Abertas',
      value: trackable ? String(opened) : '—',
      detail: trackable ? `${percent(opened, trackable)} de ${trackable} rastreáveis` : 'app 1.12+',
    },
  ];

  const undelivered = [
    { label: 'App desinstalado ou token expirado (marcados inativos)', count: recipientsGone ? stats?.invalidToken ?? 0 : metrics.invalidToken },
    { label: 'Já inativos, não enviados', count: recipientsGone ? stats?.skippedInvalid ?? 0 : metrics.skippedInvalid },
    ...metrics.failuresByCode.map(failure => ({ label: `Falhou: ${failure.label}`, count: failure.count })),
  ].filter(item => item.count > 0);

  return (
    <div style={styles.wrapper}>
      <div style={styles.card}>
        <button style={styles.backButton} onClick={onBack}>
          <ArrowBackIcon style={{ fontSize: 18 }} /> Histórico
        </button>

        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>
              {campaign.name}
              <span style={{ ...styles.chip, ...TONE_COLORS[status.tone] }}>{status.label}</span>
              {campaign.isTest && <span style={{ ...styles.chip, ...TONE_COLORS.muted }}>Teste</span>}
            </h2>
            <div style={styles.meta}>
              Criada em {formatDateTime(campaign.createdAt)}
              {campaign.createdBy?.email && ` por ${campaign.createdBy.email}`}
              {campaign.scheduledAt && ` · agendada para ${formatDateTime(campaign.scheduledAt)}`}
              {campaign.sentAt && ` · enviada em ${formatDateTime(campaign.sentAt)}`}
            </div>
            <div style={styles.meta} title="Use este ID para filtrar a campanha na aba Relatórios do Cloud Messaging no console do Firebase">
              ID da campanha: <code>{campaign.id}</code>
            </div>
          </div>
          <div style={styles.actions}>
            {campaign.status === 'scheduled' && (
              <button style={styles.cancelButton} onClick={handleCancel} disabled={canceling}>
                <EventBusyIcon style={{ fontSize: 18, marginRight: 6 }} />
                {canceling ? 'Cancelando...' : 'Cancelar agendamento'}
              </button>
            )}
            <button style={styles.duplicateButton} onClick={() => onDuplicate(campaign)}>
              <ContentCopyIcon style={{ fontSize: 18, marginRight: 6 }} /> Duplicar
            </button>
          </div>
        </div>

        {[...campaign.warnings, ...(campaign.error ? [campaign.error] : [])].map(message => (
          <div key={message} style={styles.warning}>
            <WarningAmberIcon style={{ fontSize: 18, flexShrink: 0 }} />
            <span>{message}</span>
          </div>
        ))}

        <div style={styles.funnel}>
          {funnel.map(step => (
            <div key={step.label} style={styles.funnelStep}>
              <div style={styles.funnelLabel}>{step.label}</div>
              <div style={styles.funnelValue}>{step.value}</div>
              <div style={styles.funnelDetail}>{step.detail}</div>
            </div>
          ))}
        </div>
        {trackable === 0 && sent > 0 && (
          <div style={styles.note}>
            Recebimento e abertura passam a aparecer quando os lojistas atualizarem para o app 1.12.
          </div>
        )}

        {undelivered.length > 0 && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Por que alguns não receberam</h3>
            {undelivered.map(item => (
              <div key={item.label} style={styles.undeliveredRow}>
                <span>{item.label}</span>
                <strong>{item.count}</strong>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={styles.card}>
        <h3 style={styles.sectionTitle}>Conteúdo e opções</h3>
        <table style={styles.table}>
          <tbody>
            {options.map(([label, value]) => (
              <tr key={label}>
                <td style={styles.labelCell}>{label}</td>
                <td style={styles.valueCell}>{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={styles.card}>
        <h3 style={styles.sectionTitle}>Destinatários</h3>
        {recipientsGone ? (
          <p style={styles.note}>Lista de destinatários apagada após 6 meses. Os números acima ficaram guardados na campanha.</p>
        ) : (
          <>
            <div style={styles.filters}>
              {RECIPIENT_FILTERS.map(item => {
                const count = recipients.filter(item.matches).length;
                const active = item.value === filter;
                return (
                  <button
                    key={item.value}
                    style={active ? { ...styles.filterChip, ...styles.filterChipActive } : styles.filterChip}
                    onClick={() => setFilter(item.value)}
                  >
                    {item.label} ({count})
                  </button>
                );
              })}
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar negócio ou aparelho"
                style={styles.search}
              />
            </div>
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Negócio</th>
                    <th style={styles.th}>Aparelho</th>
                    <th style={styles.th}>Situação</th>
                    <th style={styles.th}>Enviada</th>
                    <th style={styles.th}>Recebida</th>
                    <th style={styles.th}>Aberta</th>
                  </tr>
                </thead>
                <tbody>
                  {shownRecipients.map(recipient => {
                    const situation = recipientSituation(recipient);
                    return (
                      <tr key={recipient.id}>
                        <td style={styles.td}>{recipient.businessName}</td>
                        <td style={styles.td}>
                          {recipient.deviceLabel}
                          <span style={styles.muted}> · {recipient.platform === 'ios' ? 'iOS' : recipient.platform === 'android' ? 'Android' : '?'}</span>
                        </td>
                        <td style={styles.td}>
                          <span style={{ ...styles.chip, ...TONE_COLORS[situation.tone] }}>{situation.label}</span>
                        </td>
                        <td style={styles.td}>{formatDateTime(recipient.sentAt)}</td>
                        <td style={styles.td}>{formatDateTime(recipient.receivedAt)}</td>
                        <td style={styles.td}>{formatDateTime(recipient.openedAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {shownRecipients.length === 0 && <p style={styles.note}>Nenhum destinatário com esse filtro.</p>}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  card: {
    background: 'white',
    padding: '30px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  backButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 0',
    border: 'none',
    background: 'transparent',
    color: theme.colors.primary,
    fontWeight: 'bold',
    cursor: 'pointer',
    marginBottom: '15px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '15px',
    flexWrap: 'wrap',
  },
  title: {
    fontSize: '22px',
    fontWeight: 'bold',
    color: theme.colors.primary,
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
  },
  meta: {
    fontSize: '13px',
    color: '#888',
    marginTop: '6px',
  },
  chip: {
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
  },
  actions: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
  },
  cancelButton: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '10px 18px',
    borderRadius: '8px',
    border: '2px solid #c62828',
    background: 'white',
    color: '#c62828',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  duplicateButton: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '10px 18px',
    borderRadius: '8px',
    border: `2px solid ${theme.colors.primary}`,
    background: 'white',
    color: theme.colors.primary,
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  warning: {
    display: 'flex',
    gap: '8px',
    alignItems: 'flex-start',
    marginTop: '15px',
    padding: '10px 14px',
    background: '#fff8e1',
    color: '#8a5300',
    borderRadius: '8px',
    fontSize: '13px',
  },
  funnel: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: '15px',
    marginTop: '25px',
  },
  funnelStep: {
    background: '#f7f9fc',
    borderRadius: '10px',
    padding: '16px',
  },
  funnelLabel: {
    fontSize: '13px',
    color: theme.colors.secondary,
  },
  funnelValue: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: theme.colors.primary,
    margin: '4px 0',
  },
  funnelDetail: {
    fontSize: '12px',
    color: '#888',
  },
  note: {
    marginTop: '12px',
    fontSize: '13px',
    color: '#888',
  },
  section: {
    marginTop: '25px',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: theme.colors.primary,
    margin: '0 0 12px',
  },
  undeliveredRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 0',
    borderBottom: '1px solid #f0f0f0',
    fontSize: '14px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
  },
  labelCell: {
    padding: '8px 12px 8px 0',
    color: theme.colors.secondary,
    whiteSpace: 'nowrap',
    verticalAlign: 'top',
    borderBottom: '1px solid #f0f0f0',
    width: '180px',
  },
  valueCell: {
    padding: '8px 0',
    color: theme.colors.text,
    wordBreak: 'break-word',
    borderBottom: '1px solid #f0f0f0',
  },
  thumbnail: {
    maxWidth: '120px',
    maxHeight: '80px',
    borderRadius: '6px',
    border: '1px solid #e0e0e0',
  },
  filters: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
    alignItems: 'center',
    marginBottom: '15px',
  },
  filterChip: {
    padding: '6px 14px',
    borderRadius: '16px',
    border: '1px solid #ddd',
    background: 'white',
    color: theme.colors.text,
    fontSize: '13px',
    cursor: 'pointer',
  },
  filterChipActive: {
    background: theme.colors.primary,
    borderColor: theme.colors.primary,
    color: 'white',
  },
  search: {
    marginLeft: 'auto',
    minWidth: '220px',
    padding: '8px 12px',
    fontSize: '14px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    outline: 'none',
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  th: {
    textAlign: 'left',
    padding: '10px 12px',
    color: theme.colors.secondary,
    fontWeight: 600,
    borderBottom: '2px solid #eee',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '12px',
    borderBottom: '1px solid #f0f0f0',
    whiteSpace: 'nowrap',
  },
  muted: {
    color: '#999',
  },
};

export default PushCampaignDetail;
